import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Bell, Loader2, ShieldCheck } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', slate: '#5C6B7A' };
const fmt$ = (v) => `$${Math.round(Math.abs(v || 0)).toLocaleString()}`;

export default function DriftAlertCard({ api }) {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get('/claims/reserving/drift').then(r => setData(r.data)).catch(() => setData({ config: {}, latest: null }));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: check } = await api.post('/claims/reserving/drift/run');
      toast[check.alerted ? 'warning' : 'success'](check.alerted
        ? 'Drift alert raised — ARIA posted it to the governance feed'
        : 'Drift check complete — reserve gap within tolerance');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Drift check failed'); }
    setRunning(false);
  };

  if (!data) return null;
  const { config = {}, latest } = data;
  const alerted = !!latest?.alerted;
  const accent = alerted ? C.red : C.green;

  return (
    <Card className="p-4 rounded-sm bg-white flex items-start gap-3 flex-wrap" data-testid="drift-alert-card"
      style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: `3px solid ${accent}` }}>
      {alerted
        ? <Bell className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.red }} />
        : <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.green }} />}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>
          ARIA reserve drift watch
        </p>
        {latest ? (
          <p className="text-xs mt-1" style={{ color: alerted ? '#B03230' : C.slate }} data-testid="drift-alert-status">
            {alerted
              ? <>Alert raised — {(latest.reasons || []).join('; ')}. Posted to the governance feed.</>
              : <>Reserve gap {latest.net_gap > 0 ? '+' : latest.net_gap < 0 ? '−' : ''}{fmt$(latest.net_gap)} is within tolerance
                  {latest.swing != null && <> · moved {fmt$(latest.swing)} since the previous check</>}.</>}
            <span className="ml-1" style={{ color: C.slate }}>
              Alerts fire at a {fmt$(config.gap_threshold)} gap or a {fmt$(config.swing_threshold)} swing
              {config.enabled === false && ' — currently switched off'} · last check {String(latest.at || '').slice(0, 16).replace('T', ' ')} UTC
            </span>
          </p>
        ) : (
          <p className="text-xs mt-1" style={{ color: C.slate }} data-testid="drift-alert-status">
            No drift checks yet — ARIA sweeps every 6 hours, or run one now. Alerts fire at a {fmt$(config.gap_threshold)} gap
            or a {fmt$(config.swing_threshold)} swing.
          </p>
        )}
      </div>
      <button onClick={runNow} disabled={running} data-testid="drift-run-check-btn"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-[11px] font-semibold transition-colors hover:bg-[#F5F7FA] disabled:opacity-50"
        style={{ color: C.blue, borderColor: 'rgba(26,76,124,0.3)' }}>
        {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
        Run check
      </button>
    </Card>
  );
}
